import DeleteButton from "@/shared/dialogs/DeleteButton";
import {
  CircularProgress,
  Stack,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Divider,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Plus, CircleDashed, CircleX, CircleCheckBig } from "lucide-react";
import React, { useState, useEffect, Fragment } from "react";
import HolidayRequest, { vacationTypes } from "./HolidayRequest";
import ApiService from "@/api/ApiService";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import StyledModal from "@/components/planning/dialogs/newMaterialOrder/StyledModal";
import { AxiosResponse } from "axios";
import { LoadingButton } from "@mui/lab";
import { Block, Check } from "@mui/icons-material";

const today = dayjs().format("YYYY-MM-DD");

const formatRange = (a, b) => (a === b ? a : `${a} bis ${b}`);

function HolidayAction({ resourceId, close }: { resourceId: number; close: () => void }) {
  const [page, setPage] = useState<"create" | "list">("list");

  const resQuery = useQuery<any[], any, any[]>({
    queryKey: ["getStaffMember", resourceId],
    queryFn: ApiService.getEntity("staffMember", resourceId, true, "shortInfo"),
  });

  const query = useQuery<any[], any, any[]>({
    queryKey: ["getVacationRequests", resourceId],
    queryFn: ApiService.getEntitiesWithFilter("vacationRequest", { staffMember: ["staffMember", resourceId], deletedAt: null }, "calendar"),
    select: (response) => (!!response && response.length > 0 ? response : []),
  });

  const [dialogState, setDialogState] = useState(null);

  const queryClient = useQueryClient();

  const respondMutation = useMutation<any, AxiosResponse, { id: number; status: "reject" | "accept"; comment: string }>({
    mutationFn: ApiService.answerVacationRequest(),
    onSettled: (answer) => {
      setDialogState(null);
      queryClient.invalidateQueries({ queryKey: ["getCalendar"] });
      queryClient.invalidateQueries({ queryKey: ["getVacationRequests"] });
    },
  });

  useEffect(() => {
    if (query.isSuccess && query.data.length === 0) setPage("create");
  }, [query.isSuccess, query.data]);

  if (query.isLoading || query.isError || resQuery.isLoading || resQuery.isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 3,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (page === "list") {
    return (
      <Stack spacing={"5px"}>
        <Typography>{resQuery.data.name}</Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            Anträge
          </Typography>

          <Button variant="outlined" size="small" onClick={() => setPage("create")}>
            <Plus />
          </Button>
        </Box>

        <List disablePadding>
          {query.data.map((rj, index) => (
            <React.Fragment key={rj.id}>
              <ListItem
                disablePadding
                sx={{
                  py: 0,
                  alignItems: "stretch",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                    py: 1,
                  }}
                >
                  <Typography variant="body1">
                    {formatRange(dayjs(rj.start, "YYYY-MM-DD").format("DD.MM.YYYY"), dayjs(rj.end, "YYYY-MM-DD").format("DD.MM.YYYY"))}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {(vacationTypes.find((t) => t.id === rj.type)?.name ?? "?") + (rj.comment.length ? ": " + rj.comment : "")}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {rj.status.status.map((x: string, idx: number) =>
                      x === "none" ? <CircleDashed key={idx} /> : x === "rejected" ? <CircleX key={idx} /> : <CircleCheckBig key={idx} />,
                    )}
                  </Box>

                  {rj.status.comments.map((x: string, idx: number) =>
                    x.length > 0 ? (
                      <Typography key={idx} variant="body2" color="text.secondary">
                        {x}
                      </Typography>
                    ) : null,
                  )}

                  <Box sx={{ flex: 1 }} />

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {rj.status.statusString === "none" && (
                      <Fragment>
                        <IconButton color="success" onClick={() => setDialogState({ id: rj.id, status: "accept", comment: "" })}>
                          <Check />
                        </IconButton>
                        <IconButton color="error" onClick={() => setDialogState({ id: rj.id, status: "reject", comment: "" })}>
                          <Block />
                        </IconButton>
                      </Fragment>
                    )}

                    {rj.status.statusString !== "accepted" && (
                      <DeleteButton
                        type="vacationRequest"
                        id={rj.id}
                        invalidate={[["getVacationRequests"], ["getCrewHolidayCalendar"], ["getCalendar", "HOLIDAYS"]]}
                        size={undefined}
                        fontSize={undefined}
                        xsx={{}}
                      />
                    )}
                  </Box>
                </Box>
              </ListItem>

              {index < query.data.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
        {!!dialogState ? (
          <Dialog open={true} maxWidth="sm" fullWidth onClose={() => setDialogState(null)}>
            <DialogTitle>{dialogState.status === "reject" ? "Urlaub ablehnen" : "Urlaub genehmigen"}</DialogTitle>
            <DialogContent>
              <TextField
                size="small"
                fullWidth
                value={dialogState.comment}
                placeholder="Weitere Informationen"
                multiline
                onChange={(e) => {
                  setDialogState((s) => ({
                    ...s,
                    comment: (e.target as HTMLInputElement).value,
                  }));
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogState(null)}>Abbrechen</Button>
              <LoadingButton
                loading={respondMutation.isPending}
                variant="contained"
                color={dialogState.status === "reject" ? "error" : "success"}
                onClick={() => {
                  respondMutation.mutate(dialogState);
                }}
              >
                {dialogState.status === "reject" ? "Ablehnen" : "Genehmigen"}
              </LoadingButton>
            </DialogActions>
          </Dialog>
        ) : null}
      </Stack>
    );
  }

  return <HolidayRequest name={resQuery.data.name} close={() => setPage("list")} resourceId={resourceId} />;
}

type HolidayModalStore = {
  value: number | null;
  show: (value: number) => void;
  close: () => void;
};

const useHolidayModalStore = create<HolidayModalStore>()(
  devtools(
    (set) => ({
      value: null,
      show: (value) => set({ value }),
      close: () => set({ value: null }),
    }),
    { name: "holidaymodal-storage" },
  ),
);

export const useHolidayModal = () => {
  const show = useHolidayModalStore((state) => state.show);

  return {
    show,
  };
};

export default function HolidayModal() {
  const value = useHolidayModalStore((state) => state.value);
  const close = useHolidayModalStore((state) => state.close);

  return (
    <StyledModal open={value !== null} onShow={() => {}} onClose={close}>
      <HolidayAction resourceId={value} close={close} />
    </StyledModal>
  );
}
